import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
// import { supabase } from '@/config/supabase';

export interface Product {
    id: string;
    business_id: string;
    name: string;
    description: string;
    category: string;
    price: number;
    cost_price?: number;
    quantity_in_stock: number;
    low_stock_threshold?: number;
    sku?: string;
    barcode?: string;
    media_urls: string[];
    is_active: boolean;
    unit_type?: string | null;
    bulk_unit_size?: number | null;
    bulk_unit_cost?: number | null;
    created_at?: string;
}

export interface Service {
    id: string;
    business_id: string;
    name: string;
    description: string;
    category: string;
    price: number;
    duration: number;
    delivery_mode: string;
    is_active: boolean;
    media_urls: string[];
    created_at?: string;
}

export interface Business {
    id: string;
    name: string;
    description?: string;
    category?: string;
    location_address?: string;
    business_email?: string;
    primary_contact_phone?: string;
    whatsapp_number?: string;
    years_in_operation?: number;
    created_at?: string;
}

export interface BusinessDocument {
    id: string;
    business_id: string;
    document_type: string;
    file_name: string;
    file_url: string;
    uploaded_at: string;
    verified: boolean;
    status?: string;
}

export interface BarcodeItem {
    id: string;
    product_id: string;
    product_name: string;
    barcode: string;
    sku?: string;
    status: 'generated' | 'printed' | 'active' | 'deactivated';
    created_at: string;
}

export interface HardwareScanner {
    id: string;
    business_id: string;
    name: string;
    device_identifier: string;
    status: 'active' | 'inactive' | 'maintenance';
    created_at: string;
    updated_at: string;
}

export interface BusinessStaff {
    id: string;
    business_id?: string;
    name: string;
    role: string;
    phone: string;
    status: 'Active' | 'On Leave' | 'Terminated' | 'Suspended' | 'Resigned' | 'Inactive';
    permissions?: string[];
    verificationStatus?: string | null;
}

export interface Analytics {
    total_revenue: number;
    total_orders: number;
    sales_by_day: { date: string; amount: number }[];
    average_rating: number;
    top_products: { id: string; name: string; sales_count: number }[];
    recent_orders: { id: string; amount: number; created_at: string; status: string }[];
}

interface BusinessState {
    products: Product[];
    services: Service[];
    businesses: Business[];
    documents: BusinessDocument[];
    activeBusiness: Business | null;
    barcodeItems: BarcodeItem[];
    scanners: HardwareScanner[];
    staff: BusinessStaff[];
    analytics: Analytics | null;
    loading: boolean;
    error: string | null;

    fetchBusinesses: () => Promise<void>;
    fetchBusiness: (id: string) => Promise<void>;
    updateBusiness: (id: string, data: any) => Promise<void>;
    setActiveBusiness: (business: Business) => void;
    createBusiness: (data: FormData) => Promise<void>;

    fetchProducts: (businessId: string, filters?: any) => Promise<void>;
    fetchServices: (businessId: string, filters?: any) => Promise<void>;
    createProduct: (businessId: string, data: Partial<Product>) => Promise<void>;
    updateProduct: (businessId: string, productId: string, data: Partial<Product>) => Promise<void>;
    deleteProduct: (businessId: string, productId: string) => Promise<void>;
    createService: (businessId: string, data: Partial<Service>) => Promise<void>;
    updateService: (businessId: string, serviceId: string, data: Partial<Service>) => Promise<void>;
    deleteService: (businessId: string, serviceId: string) => Promise<void>;

    fetchBarcodeItems: (businessId: string) => Promise<void>;
    generateBarcode: (businessId: string, productId: string, quantity?: number) => Promise<void>;
    updateBarcodeStatus: (itemId: string, status: BarcodeItem['status']) => void;

    fetchScanners: (businessId: string) => Promise<void>;
    addScanner: (businessId: string, payload: Partial<HardwareScanner>) => Promise<void>;
    updateScanner: (businessId: string, scannerId: string, payload: Partial<HardwareScanner>) => Promise<void>;
    deleteScanner: (businessId: string, scannerId: string) => Promise<void>;

    fetchStaff: (businessId: string) => Promise<void>;
    inviteStaff: (businessId: string, payload: any) => Promise<void>;
    updateStaffStatus: (staffId: string, status: string) => Promise<void>;
    removeStaffMember: (staffId: string) => Promise<void>;

    fetchAnalytics: (businessId: string, timeRange: string) => Promise<void>;

    uploadBusinessDocument: (businessId: string, type: string, file: any) => Promise<void>;
    deleteBusinessDocument: (businessId: string, docId: string) => Promise<void>;
}

export const useBusinessStore = create<BusinessState>()(
  persist(
    (set, get) => ({
      products: [],
    services: [],
    businesses: [],
    documents: [],
    activeBusiness: null,
    barcodeItems: [],
    scanners: [],
    staff: [],
    analytics: null,
    loading: false,
    error: null,

    createBusiness: async (data: FormData) => {
        set({ loading: true, error: null });
        try {
            await apiClient.post(API_ENDPOINTS.BUSINESS_CREATE, data, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            await get().fetchBusinesses();
        } catch (error: any) {
            console.error('Error creating business:', error);
            set({ error: error.message || 'Failed to create business', loading: false });
            throw error;
        }
    },

    fetchBusinesses: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.BUSINESSES);
            console.log('Fetched businesses:', response.data);
            set({ businesses: response.data, loading: false });
            // Optionally set active business if none selected and businesses exist
            if (response.data.length > 0 && !get().activeBusiness) {
                set({ activeBusiness: response.data[0] });
            }
        } catch (error: any) {
            console.error('Failed to fetch businesses:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch businesses', loading: false });
        }
    },

    fetchBusiness: async (id) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.BUSINESS_DETAILS(id));
            set({ activeBusiness: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch business details:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch business details', loading: false });
        }
    },

    updateBusiness: async (id, data) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.put(API_ENDPOINTS.BUSINESS_UPDATE(id), data);
            set(state => ({
                activeBusiness: state.activeBusiness ? { ...state.activeBusiness, ...response.data } : response.data,
                businesses: state.businesses.map(b => b.id === id ? response.data : b),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to update business:', error);
            set({ error: error.response?.data?.error || 'Failed to update business', loading: false });
            throw error;
        }
    },

    setActiveBusiness: (business) => set({ activeBusiness: business }),

    fetchProducts: async (businessId, filters) => {
        set({ loading: true, error: null });
        try {
            console.log('Fetching products for business:', businessId, 'URL:', API_ENDPOINTS.BUSINESS_PRODUCTS(businessId));
            const response = await apiClient.get(API_ENDPOINTS.BUSINESS_PRODUCTS(businessId), { params: filters });
            console.log('[ProductStore] Raw products response:', response.data);
            
            // Log first product to see structure
            if (response.data?.length > 0) {
                console.log('[ProductStore] First product sample:', {
                    id: response.data[0].id,
                    name: response.data[0].name,
                    media_urls: response.data[0].media_urls,
                    image_url: response.data[0].image_url,
                });
            }
            
            set({ products: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch products:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch products', loading: false });
        }
    },

    fetchServices: async (businessId, filters) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.BUSINESS_SERVICES(businessId), { params: filters });
            set({ services: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch services:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch services', loading: false });
            set({ services: [], loading: false });
        }
    },

    createProduct: async (businessId, data) => {
        set({ loading: true, error: null });
        try {
            const payload: any = {
                name: data.name,
                category: data.category,
                price: data.price,
                cost_price: data.cost_price,
                quantity_in_stock: data.quantity_in_stock,
                low_stock_threshold: data.low_stock_threshold || 5,
                description: data.description,
                sku: data.sku,
                is_active: data.is_active !== undefined ? data.is_active : true,
                manufacturer_barcode: (data as any).barcode || null,
                is_perishable: false, // Default to match mobile app's minimal payload
                media_urls: data.media_urls || []
            };

            // Remove purely undefined/null keys to match the precise mobile app logic
            Object.keys(payload).forEach(key => (payload as any)[key] === undefined && delete (payload as any)[key]);

            console.log('Creating product via API backend:', payload);

            const response = await apiClient.post(API_ENDPOINTS.BUSINESS_PRODUCTS(businessId), payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const newProduct = response.data;

            set(state => ({
                products: [newProduct, ...state.products], // Prepend to list
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to create product:', error);
            set({ error: error.response?.data?.error || error.message || 'Failed to create product', loading: false });
            throw error;
        }
    },

    updateProduct: async (businessId, productId, data) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.put(API_ENDPOINTS.BUSINESS_UPDATE_PRODUCT(businessId, productId), data);
            set(state => ({
                products: state.products.map(p => p.id === productId ? response.data : p),
                loading: false,
            }));
        } catch (error: any) {
            console.error('Failed to update product:', error);
            set({ error: error.response?.data?.error || 'Failed to update product', loading: false });
            throw error;
        }
    },

    deleteProduct: async (businessId, productId) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(API_ENDPOINTS.BUSINESS_DELETE_PRODUCT(businessId, productId));
            set(state => ({ products: state.products.filter(p => p.id !== productId), loading: false }));
        } catch (error: any) {
            console.error('Failed to delete product:', error);
            set({ error: error.response?.data?.error || 'Failed to delete product', loading: false });
            throw error;
        }
    },

    createService: async (businessId, data) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.post(API_ENDPOINTS.BUSINESS_CREATE_SERVICE(businessId), data);
            set(state => ({ services: [...state.services, response.data], loading: false }));
        } catch (error: any) {
            console.error('Failed to create service:', error);
            set({ error: error.response?.data?.error || 'Failed to create service', loading: false });
            throw error;
        }
    },

    updateService: async (businessId, serviceId, data) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.put(API_ENDPOINTS.BUSINESS_UPDATE_SERVICE(businessId, serviceId), data);
            set(state => ({
                services: state.services.map(svc => svc.id === serviceId ? response.data : svc),
                loading: false,
            }));
        } catch (error: any) {
            console.error('Failed to update service:', error);
            set({ error: error.response?.data?.error || 'Failed to update service', loading: false });
            throw error;
        }
    },

    deleteService: async (businessId, serviceId) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(API_ENDPOINTS.BUSINESS_DELETE_SERVICE(businessId, serviceId));
            set(state => ({ services: state.services.filter(svc => svc.id !== serviceId), loading: false }));
        } catch (error: any) {
            console.error('Failed to delete service:', error);
            set({ error: error.response?.data?.error || 'Failed to delete service', loading: false });
            throw error;
        }
    },

    fetchBarcodeItems: async (businessId) => {
        set({ loading: true, error: null });
        try {
            // 1. Fetch all products first to get the list
            console.log('Fetching products to get barcodes...');
            const productsRes = await apiClient.get(API_ENDPOINTS.BUSINESS_PRODUCTS(businessId));
            const products = productsRes.data as Product[];
            set({ products }); // Update products in store

            // 2. Fetch items for each product in parallel
            console.log(`Fetching items for ${products.length} products...`);
            const itemPromises = products.map(product =>
                apiClient.get(API_ENDPOINTS.BUSINESS_PRODUCT_ITEMS(product.id))
                    .then(res => ({ items: res.data, productId: product.id }))
                    .catch(err => {
                        console.warn(`Failed to fetch items for product ${product.id}`, err);
                        return { items: [], productId: product.id };
                    })
            );

            const results = await Promise.all(itemPromises);

            // 3. Flatten and Enrich
            const allItems: BarcodeItem[] = [];
            results.forEach(({ items, productId }) => {
                const product = products.find(p => p.id === productId);
                if (Array.isArray(items)) {
                    items.forEach((item: any) => {
                        allItems.push({
                            ...item,
                            product_name: product?.name || 'Unknown',
                            sku: product?.sku,
                        });
                    });
                }
            });

            console.log(`Fetched ${allItems.length} total barcode items.`);
            set({ barcodeItems: allItems, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch barcodes:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch barcodes', loading: false });
            set({ barcodeItems: [], loading: false });
        }
    },

    generateBarcode: async (businessId, productId, quantity = 1) => {
        set({ loading: true, error: null });
        try {
            // Updated to match backend: POST /businesses/products/:businessId/:productId/generate-barcodes
            const response = await apiClient.post(API_ENDPOINTS.BUSINESS_GENERATE_BARCODE(businessId, productId), { quantity }, { timeout: 60000 });
            set(state => ({
                barcodeItems: [...state.barcodeItems, response.data],
                products: state.products.map(p => p.id === productId ? { ...p, barcode: response.data.barcode } : p),
                loading: false,
            }));
        } catch (error: any) {
            console.error('Failed to generate barcode:', error);
            set({ error: error.response?.data?.error || 'Failed to generate barcode', loading: false });
            throw error;
        }
    },

    updateBarcodeStatus: (itemId, status) => {
        // Optimistic update, or implement API call if needed
        set(state => ({
            barcodeItems: state.barcodeItems.map(b => b.id === itemId ? { ...b, status } : b),
        }));
    },

    fetchScanners: async (businessId) => {
        set({ loading: true, error: null });
        try {
            const raw = localStorage.getItem(`scanners_${businessId}`);
            const data = raw ? JSON.parse(raw) : [];
            set({ scanners: data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch scanners:', error);
            set({ error: 'Failed to fetch scanners from local storage', loading: false });
        }
    },

    addScanner: async (businessId, payload) => {
        set({ loading: true, error: null });
        try {
            const newScanner = {
                ...payload,
                id: crypto.randomUUID(),
                created_at: new Date().toISOString()
            } as HardwareScanner;

            set(state => {
                const newScanners = [newScanner, ...state.scanners];
                localStorage.setItem(`scanners_${businessId}`, JSON.stringify(newScanners));
                return { scanners: newScanners, loading: false };
            });
        } catch (error: any) {
            console.error('Failed to add scanner:', error);
            set({ error: 'Failed to save scanner locally', loading: false });
            throw error;
        }
    },

    updateScanner: async (businessId, scannerId, payload) => {
        set({ loading: true, error: null });
        try {
            set(state => {
                const newScanners = state.scanners.map(s =>
                    s.id === scannerId ? { ...s, ...payload, updated_at: new Date().toISOString() } : s
                );
                localStorage.setItem(`scanners_${businessId}`, JSON.stringify(newScanners));
                return { scanners: newScanners, loading: false };
            });
        } catch (error: any) {
            console.error('Failed to update scanner:', error);
            set({ error: 'Failed to update scanner locally', loading: false });
            throw error;
        }
    },

    deleteScanner: async (businessId, scannerId) => {
        set({ loading: true, error: null });
        try {
            set(state => {
                const newScanners = state.scanners.filter(s => s.id !== scannerId);
                localStorage.setItem(`scanners_${businessId}`, JSON.stringify(newScanners));
                return { scanners: newScanners, loading: false };
            });
        } catch (error: any) {
            console.error('Failed to delete scanner:', error);
            set({ error: 'Failed to delete scanner locally', loading: false });
            throw error;
        }
    },

    fetchStaff: async (businessId) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.BUSINESS_STAFF(businessId));
            set({ staff: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch staff:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch staff', loading: false });
        }
    },

    inviteStaff: async (businessId, payload) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.post(API_ENDPOINTS.BUSINESS_ADD_STAFF(businessId), payload);
            set(state => ({
                staff: [...state.staff, response.data],
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to invite staff:', error);
            set({ error: error.response?.data?.error || 'Failed to invite staff', loading: false });
            throw error;
        }
    },

    updateStaffStatus: async (staffId, status) => {
        set({ loading: true, error: null });
        try {
            // Updated to match backend: PUT /businesses/staff/:assignmentId/status
            await apiClient.put(API_ENDPOINTS.BUSINESS_STAFF_STATUS(staffId), { status });
            set(state => ({
                staff: state.staff.map(m => m.id === staffId ? { ...m, status: status as any } : m),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to update staff status:', error);
            set({ error: error.response?.data?.error || 'Failed to update staff status', loading: false });
            throw error;
        }
    },

    removeStaffMember: async (staffId) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(API_ENDPOINTS.BUSINESS_REMOVE_STAFF(staffId));
            set(state => ({
                staff: state.staff.filter(m => m.id !== staffId),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to remove staff member:', error);
            set({ error: error.response?.data?.error || 'Failed to remove staff member', loading: false });
            throw error;
        }
    },

    fetchAnalytics: async (businessId, timeRange) => {
        set({ loading: true, error: null });
        try {
            let fromDate = new Date();
            if (timeRange === 'day') fromDate.setDate(fromDate.getDate() - 1);
            else if (timeRange === 'week') fromDate.setDate(fromDate.getDate() - 7);
            else if (timeRange === 'month') fromDate.setMonth(fromDate.getMonth() - 1);
            else if (timeRange === 'year') fromDate.setFullYear(fromDate.getFullYear() - 1);

            const response = await apiClient.get(API_ENDPOINTS.BUSINESS_SALES(businessId), {
                params: { limit: 100, status: 'completed', from: fromDate.toISOString() }
            });

            const sales = response.data.sales || [];

            const total_revenue = sales.reduce((sum: number, sale: any) => sum + (sale.total_amount || 0), 0);
            const total_orders = sales.length;

            const salesByDayMap = new Map<string, number>();
            sales.forEach((sale: any) => {
                const date = new Date(sale.created_at).toISOString().split('T')[0];
                salesByDayMap.set(date, (salesByDayMap.get(date) || 0) + (sale.total_amount || 0));
            });
            const sales_by_day = Array.from(salesByDayMap.entries())
                .map(([date, amount]) => ({ date, amount }))
                .sort((a, b) => a.date.localeCompare(b.date));

            const productSalesMap = new Map<string, { id: string, name: string, count: number }>();
            sales.forEach((sale: any) => {
                sale.sale_items?.forEach((item: any) => {
                    const productId = item.product_id || item.products?.id;
                    const productName = item.products?.name || 'Unknown Product';
                    if (productId) {
                        const existing = productSalesMap.get(productId) || { id: productId, name: productName, count: 0 };
                        existing.count += (item.quantity || 1);
                        productSalesMap.set(productId, existing);
                    }
                });
            });
            const top_products = Array.from(productSalesMap.values())
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
                .map(p => ({ id: p.id, name: p.name, sales_count: p.count }));

            const recent_orders = sales.slice(0, 5).map((sale: any) => ({
                id: sale.id,
                amount: sale.total_amount,
                created_at: sale.created_at,
                status: sale.status
            }));

            set({
                analytics: {
                    total_revenue,
                    total_orders,
                    sales_by_day,
                    average_rating: 4.8,
                    top_products,
                    recent_orders
                },
                loading: false
            });
        } catch (error: any) {
            console.error('Failed to fetch analytics:', error);
            set({
                analytics: {
                    total_revenue: 0,
                    total_orders: 0,
                    sales_by_day: [],
                    average_rating: 0,
                    top_products: [],
                    recent_orders: []
                },
                loading: false
            });
        }
    },

    uploadBusinessDocument: async (businessId, type, file) => {
        set({ loading: true, error: null });
        try {
            const formData = new FormData();
            formData.append('document_type', type);
            formData.append('file', file);

            // Adjust header for FormData if necessary, though apiClient might handle it
            const response = await apiClient.post(API_ENDPOINTS.BUSINESS_UPLOAD_DOCUMENT(businessId), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            set(state => ({ documents: [...state.documents, response.data], loading: false }));
        } catch (error: any) {
            console.error('Failed to upload document:', error);
            set({ error: error.response?.data?.error || 'Failed to upload document', loading: false });
            throw error;
        }
    },

    deleteBusinessDocument: async (businessId, docId) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(API_ENDPOINTS.BUSINESS_DELETE_DOCUMENT(businessId, docId));
            set(state => ({ documents: state.documents.filter(d => d.id !== docId), loading: false }));
        } catch (error: any) {
            console.error('Failed to delete document:', error);
            set({ error: error.response?.data?.error || 'Failed to delete document', loading: false });
            throw error;
        }
    }
  }),
  {
    name: 'business-store',
    partialize: (state) => ({ 
      products: state.products, 
      services: state.services,
      barcodeItems: state.barcodeItems
    }),
  }
));
